import React, { useState } from "react";
import { Trophy, Activity, ChevronDown, ChevronUp, Star, Medal, Zap, Crown, Users, Lock, CheckCircle, Filter, Search, TrendingUp, Sparkles, Gift } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation, useFormat } from "../i18n";

// Mock achievements data with different categories and unlock status
const achievementsData = {
  teamwork: {
    id: "teamwork",
    title: "Teamwork & Collaboration",
    color: "from-blue-500 to-purple-600",
    bgColor: "bg-blue-50",
    icon: <Users className="w-6 h-6" />,
    completed: 3,
    total: 5,
    achievements: [
      { id: 1, key: "team_player", title: "Team Player", description: "Collaborate on 10 projects", badge: "🤝", unlocked: true, rarity: "common", points: 50 },
      { id: 2, key: "mentor", title: "Mentor", description: "Help 5 team members", badge: "👨‍🏫", unlocked: true, rarity: "rare", points: 100 },
      { id: 3, key: "bridge_builder", title: "Bridge Builder", description: "Connect different departments", badge: "🌉", unlocked: true, rarity: "epic", points: 150 },
      { id: 4, key: "unity_master", title: "Unity Master", description: "Lead cross-functional initiative", badge: "🔗", unlocked: false, rarity: "legendary", points: 250 },
      { id: 5, key: "harmony_keeper", title: "Harmony Keeper", description: "Resolve 20 team conflicts", badge: "☮️", unlocked: false, rarity: "legendary", points: 300 }
    ]
  },
  innovation: {
    id: "innovation",
    title: "Innovation & Creativity",
    color: "from-purple-500 to-pink-600",
    bgColor: "bg-purple-50",
    icon: <Zap className="w-6 h-6" />,
    completed: 2,
    total: 4,
    achievements: [
      { id: 6, key: "idea_generator", title: "Idea Generator", description: "Submit 20 improvement suggestions", badge: "💡", unlocked: true, rarity: "common", points: 75 },
      { id: 7, key: "innovation_champion", title: "Innovation Champion", description: "Implement game-changing solution", badge: "🚀", unlocked: true, rarity: "epic", points: 200 },
      { id: 8, key: "future_thinker", title: "Future Thinker", description: "Predict and prepare for trends", badge: "🔮", unlocked: false, rarity: "legendary", points: 350 },
      { id: 9, key: "disruptor", title: "Disruptor", description: "Revolutionize a core process", badge: "⚡", unlocked: false, rarity: "mythical", points: 500 }
    ]
  },
  leadership: {
    id: "leadership",
    title: "Leadership & Growth",
    color: "from-yellow-500 to-orange-600",
    bgColor: "bg-yellow-50",
    icon: <Crown className="w-6 h-6" />,
    completed: 1,
    total: 4,
    achievements: [
      { id: 10, key: "rising_star", title: "Rising Star", description: "Show consistent growth", badge: "⭐", unlocked: true, rarity: "rare", points: 100 },
      { id: 11, key: "inspiring_leader", title: "Inspiring Leader", description: "Lead a successful project", badge: "👑", unlocked: false, rarity: "epic", points: 200 },
      { id: 12, key: "visionary", title: "Visionary", description: "Set strategic direction", badge: "🎯", unlocked: false, rarity: "legendary", points: 300 },
      { id: 13, key: "game_changer", title: "Game Changer", description: "Transform company culture", badge: "🏆", unlocked: false, rarity: "mythical", points: 500 }
    ]
  },
  excellence: {
    id: "excellence",
    title: "Quality & Excellence",
    color: "from-green-500 to-teal-600",
    bgColor: "bg-green-50",
    icon: <Medal className="w-6 h-6" />,
    completed: 4,
    total: 4,
    achievements: [
      { id: 14, key: "perfectionist", title: "Perfectionist", description: "Zero defects for 30 days", badge: "💎", unlocked: true, rarity: "rare", points: 120 },
      { id: 15, key: "quality_guardian", title: "Quality Guardian", description: "Maintain 99% accuracy", badge: "🛡️", unlocked: true, rarity: "epic", points: 180 },
      { id: 16, key: "excellence_master", title: "Excellence Master", description: "Lead quality improvements", badge: "👑", unlocked: true, rarity: "epic", points: 200 },
      { id: 17, key: "standard_setter", title: "Standard Setter", description: "Define new quality benchmarks", badge: "📏", unlocked: true, rarity: "legendary", points: 400 }
    ]
  }
};

// Recent unlocks for activity feed
const HOUR_MS = 60 * 60 * 1000;
const recentUnlocks = [
  { user: "Michael Baskhairoun", achievementKey: "innovation_champion", badge: "🚀", agoMs: 2 * HOUR_MS, points: 200 },
  { user: "Caroline Mikhail", achievementKey: "standard_setter", badge: "📏", agoMs: 24 * HOUR_MS, points: 400 },
  { user: "Raghav Bhalla", achievementKey: "team_player", badge: "🤝", agoMs: 48 * HOUR_MS, points: 50 },
  { user: "Rofaiel Tadrous", achievementKey: "quality_guardian", badge: "🛡️", agoMs: 72 * HOUR_MS, points: 180 }
];

export default function CreativeAchievementsPage() {
  const { t } = useTranslation("profile");
  const fmt = useFormat();
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Calculate total stats
  const totalUnlocked = Object.values(achievementsData).reduce((sum, category) => sum + category.completed, 0);
  const totalAchievements = Object.values(achievementsData).reduce((sum, category) => sum + category.total, 0);
  const totalPoints = Object.values(achievementsData)
    .flatMap(cat => cat.achievements)
    .filter(achievement => achievement.unlocked)
    .reduce((sum, achievement) => sum + achievement.points, 0);

  return (
    <div className="min-h-screen bg-gray-50 pb-8 pt-3 sm:pt-8 px-4 sm:px-6 lg:px-8">
      {/* Search and action bar */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-end mb-6">
          {/* Search and Filter */}
          <div className="flex gap-3 mt-4 lg:mt-0">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder={t("achievements.search_placeholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-2 bg-white border border-gray-300 rounded-full hover:bg-gray-50 flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              {t("achievements.filter")}
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatsCard
            title={t("achievements.stats.unlocked")}
            value={t("achievements.stats.unlocked_value", { unlocked: totalUnlocked, total: totalAchievements })}
            icon={<Trophy className="w-6 h-6 text-yellow-500" />}
            gradient="from-yellow-500 to-orange-500"
          />
          <StatsCard
            title={t("achievements.stats.total_points")}
            value={fmt.number(totalPoints)}
            icon={<Star className="w-6 h-6 text-purple-500" />}
            gradient="from-purple-500 to-pink-500"
          />
          <StatsCard
            title={t("achievements.stats.this_month")}
            value={fmt.number(8)}
            icon={<TrendingUp className="w-6 h-6 text-green-500" />}
            gradient="from-green-500 to-teal-500"
          />
          <StatsCard
            title={t("achievements.stats.streak")}
            value={t("achievements.stats.days", { count: 12 })}
            icon={<Sparkles className="w-6 h-6 text-blue-500" />}
            gradient="from-blue-500 to-indigo-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Main Achievement Categories */}
        <div className="xl:col-span-3 space-y-6">
          {Object.entries(achievementsData).map(([key, category]) => (
            <AchievementCategory key={key} category={category} />
          ))}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <RecentActivity />
          <UpcomingChallenges />
        </div>
      </div>
    </div>
  );
}

const StatsCard = ({ title, value, icon, gradient }) => (
  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
        {icon}
      </div>
    </div>
  </div>
);

const AchievementCategory = ({ category }) => {
  const { t } = useTranslation("profile");
  const fmt = useFormat();
  const [isExpanded, setIsExpanded] = useState(true);
  const progressPercentage = (category.completed / category.total) * 100;

  return (
    <motion.div 
      className="bg-white rounded-2xl shadow-md overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Category Header */}
      <div className={`bg-gradient-to-r ${category.color} p-6 text-white`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {category.icon}
            <h2 className="text-xl font-bold">{t(`achievements.categories.${category.id}`)}</h2>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-full hover:bg-white/20 transition-colors"
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
        
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>{t("achievements.progress.completed_of", { completed: category.completed, total: category.total })}</span>
            <span>{fmt.number(Math.round(progressPercentage) / 100, { style: "percent" })}</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2">
            <motion.div 
              className="bg-white h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </div>
        </div>
      </div>

      {/* Achievements Grid */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.achievements.map((achievement) => (
                  <AchievementCard key={achievement.id} achievement={achievement} />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const AchievementCard = ({ achievement }) => {
  const { t } = useTranslation("profile");
  const [showDetails, setShowDetails] = useState(false);

  const getRarityColor = (rarity) => {
    switch(rarity) {
      case "common": return "bg-gray-100 text-gray-700 border-gray-300";
      case "rare": return "bg-blue-100 text-blue-700 border-blue-300";
      case "epic": return "bg-purple-100 text-purple-700 border-purple-300";
      case "legendary": return "bg-yellow-100 text-yellow-700 border-yellow-300";
      case "mythical": return "bg-pink-100 text-pink-700 border-pink-300";
      default: return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  return (
    <motion.div
      className={`relative rounded-2xl border-2 p-4 transition-all duration-300 cursor-pointer ${
        achievement.unlocked 
          ? "bg-white shadow-md hover:shadow-lg" 
          : "bg-gray-50 border-dashed border-gray-300"
      }`}
      whileHover={{ scale: achievement.unlocked ? 1.02 : 1 }}
      onClick={() => achievement.unlocked && setShowDetails(!showDetails)}
    >
      {/* Lock overlay for locked achievements */}
      {!achievement.unlocked && (
        <div className="absolute inset-0 bg-gray-100/80 rounded-2xl flex items-center justify-center">
          <Lock className="w-8 h-8 text-gray-400" />
        </div>
      )}

      {/* Achievement Content */}
      <div className="text-center">
        <div className={`text-4xl mb-3 ${!achievement.unlocked ? "grayscale opacity-50" : ""}`}>
          {achievement.badge}
        </div>
        
        <h3 className={`font-bold text-lg mb-2 ${achievement.unlocked ? "text-gray-900" : "text-gray-500"}`}>
          {t(`badges.${achievement.key}.title`)}
        </h3>
        
        <p className={`text-sm mb-3 ${achievement.unlocked ? "text-gray-600" : "text-gray-400"}`}>
          {t(`badges.${achievement.key}.description`)}
        </p>

        {/* Rarity and Points */}
        <div className="flex justify-between items-center">
          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRarityColor(achievement.rarity)}`}>
            {t(`badges.rarity.${achievement.rarity}`)}
          </span>
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-medium">{achievement.points}</span>
          </div>
        </div>

        {/* Unlock indicator */}
        {achievement.unlocked && (
          <div className="absolute top-2 right-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
        )}
      </div>
    </motion.div>
  );
};

const RecentActivity = () => {
  const { t } = useTranslation("profile");
  const fmt = useFormat();
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden">
      <div className="p-4 flex items-center justify-between org-gradient">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-white" />
          <h3 className="font-bold text-white">{t("achievements.recent_unlocks")}</h3>
        </div>
        <button 
          onClick={() => setExpanded(!expanded)}
          className="text-white p-1 rounded-full hover:bg-white/20"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="p-4 space-y-4">
              {recentUnlocks.map((unlock, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl">
                  <div className="text-2xl">{unlock.badge}</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{unlock.user}</p>
                    <p className="text-xs text-gray-600">{t("achievements.activity.unlocked", { achievement: t(`badges.${unlock.achievementKey}.title`) })}</p>
                    <p className="text-xs text-gray-500">{fmt.relative(Date.now() - unlock.agoMs)}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-yellow-500" />
                      <span className="text-xs font-medium">{t("achievements.points_plus", { points: unlock.points })}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const UpcomingChallenges = () => {
  const { t } = useTranslation("profile");
  const fmt = useFormat();
  const challenges = [
    { title: t("achievements.challenges.items.collaborations.title"), progress: 3, total: 5, reward: t("achievements.challenges.items.collaborations.reward"), points: 150 },
    { title: t("achievements.challenges.items.ideas.title"), progress: 7, total: 10, reward: t("achievements.challenges.items.ideas.reward"), points: 200 },
    { title: t("achievements.challenges.items.mentor.title"), progress: 1, total: 3, reward: t("achievements.challenges.items.mentor.reward"), points: 250 }
  ];

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden">
      <div className="p-4 bg-gradient-to-r from-green-500 to-teal-600">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-white" />
          <h3 className="font-bold text-white">{t("achievements.challenges.title")}</h3>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {challenges.map((challenge, index) => (
          <div key={index} className="border border-gray-200 rounded-2xl p-3">
            <h4 className="font-medium text-gray-900 text-sm mb-2">{challenge.title}</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-gray-600">
                <span>{t("achievements.challenges.progress", { progress: challenge.progress, total: challenge.total })}</span>
                <span>{fmt.number(Math.round((challenge.progress / challenge.total) * 100) / 100, { style: "percent" })}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className="bg-gradient-to-r from-green-500 to-teal-500 h-1.5 rounded-full"
                  style={{ width: `${(challenge.progress / challenge.total) * 100}%` }}
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">{challenge.reward}</span>
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 text-yellow-500" />
                  <span className="text-xs font-medium">{t("achievements.points_plus", { points: challenge.points })}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};